import * as Sentry from "@sentry/react";
import "react-app-polyfill/stable";
import "./tailwind.css";
import React from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  BrowserRouter,
} from "react-router-dom";

import { createRoot } from "react-dom/client";

import App from "./App";
import { sendWebVitalsToSentry } from "@/utils/reportWebVitals";
import { ThemeProvider } from "@/contexts/ThemeContext";
// import { getStoredSentryUser } from "@/utils/sentryUserStorage";

// const isAuthPage = () => {
//   const pathname = window.location.pathname;
//   return pathname === "/" || pathname.includes("/auth/signin");
// };

// const shouldAttachFeedbackIntegration = () => {
//   if (!isAuthPage()) {
//     return true;
//   }
//   return Boolean(getStoredSentryUser());
// };

// Initialize Sentry if DSN is provided, regardless of environment
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    // Setting this option to true will send default PII data to Sentry(company not our dashboard).
    sendDefaultPii: false,

    attachStacktrace: true, // Enable stacktrace collection for better error reporting

    release: "revpilot@" + import.meta.env.VITE_APP_VERSION,

    integrations: [
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        xhr: true,
      }),
      Sentry.browserApiErrorsIntegration({
        setTimeout: true,
        setInterval: true,
        requestAnimationFrame: true,
        XMLHttpRequest: true,
        eventTarget: true,
        unregisterOriginalCallbacks: true,
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      Sentry.browserProfilingIntegration(),
      Sentry.browserSessionIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
      Sentry.replayCanvasIntegration(),
      // TODO: Re-enable feedbackIntegration when the floating button no longer blocks table row navigation.
      // ...(shouldAttachFeedbackIntegration()
      //   ? [
      //       Sentry.feedbackIntegration({
      //         colorScheme: "system",
      //         isNameRequired: true,
      //         isEmailRequired: true,
      //         showBranding: false,
      //         screenshot: false,
      //       }),
      //     ]
      //   : []),
      Sentry.httpClientIntegration(),
    ],

    // Tracing
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1
    ),
    tracePropagationTargets: ["localhost", /^https:\/\/www\.atica\.ai/],
    replaysSessionSampleRate: Number(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0.05
    ),
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event) {
      if (event.request?.url?.includes("/auth/")) {
        return null;
      }
      // Drop expected HTTP errors — auth expiry (401), RBAC (403), conflicts (409), rate limits (429)
      const statusCode =
        event.contexts?.response?.status_code ??
        event.tags?.["http.status_code"];
      if ([401, 403, 409, 429].includes(Number(statusCode))) {
        return null;
      }

      // Remove sensitive headers
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.Cookie;
      }

      if (event.exception) {
        event.tags = {
          ...event.tags,
          errorSource: "frontend",
        };
      }

      return event;
    },
    beforeSendTransaction(event) {
      // Filter out noisy transactions
      if (event.transaction?.includes("heartbeat")) {
        return null;
      }
      return event;
    },
  });
}

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);

sendWebVitalsToSentry();
